import PatientPHIStyle from './PatientPHI.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import CommonAgGrid from '../../components/CommonAgGrid/CommonAgGrid';
import DeleteDialog from '../../components/DeleteDialog';
import { deletePatientPHI, getAllPatientPHI } from '../../apis/patientPHISlice';

export default function PatientPHIList({ search }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [localSearch, setLocalSearch] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  const { loggedIn } = useSelector((state) => state.authData);
  const { records, loading, totalCount } = useSelector((state) => state.patientPHIData);

  const searchTerm = (localSearch || search || '').trim();

  const callApi = useCallback(() => {
    dispatch(getAllPatientPHI({ page, pageSize, search: searchTerm }));
  }, [dispatch, page, pageSize, searchTerm]);

  useEffect(() => {
    callApi();
  }, [callApi, loggedIn]);

  const handlePaginationModelChange = (model) => {
    setPage(model.page);
    setPageSize(model.pageSize);
  };

  const handleDelete = async (data) => {
    const response = await dispatch(deletePatientPHI(data._id));
    if (response?.payload?.success) {
      toast.success(response?.payload.message);
      callApi();
    } else {
      toast.error(response?.payload?.message || 'Error deleting patient PHI');
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteRow) return;
    await handleDelete(deleteRow);
    setDeleteOpen(false);
    setDeleteRow(null);
  };

  const columns = useMemo(
    () => [
      { field: 'patientId', headerName: 'Patient ID', minWidth: 120, flex: 0.7 },
      { field: 'fullName', headerName: 'Name', minWidth: 160, flex: 1.1 },
      { field: 'mobileNumber', headerName: 'Mobile', minWidth: 120, flex: 0.8 },
      { field: 'gender', headerName: 'Gender', minWidth: 90, flex: 0.6 },
      { field: 'age', headerName: 'Age', minWidth: 70, flex: 0.45 },
      { field: 'city', headerName: 'City', minWidth: 110, flex: 0.7 },
      {
        field: 'createdAt',
        headerName: 'Created',
        minWidth: 120,
        flex: 0.7,
        valueGetter: (p) => (p.data?.createdAt ? moment(p.data.createdAt).format('DD-MM-YYYY') : '-'),
      },
      {
        colId: 'actions',
        headerName: 'Action',
        width: 132,
        flex: 0,
        minWidth: 132,
        maxWidth: 132,
        sortable: false,
        pinned: 'right',
        cellRenderer: (params) => (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Tooltip title="View">
              <IconButton
                onClick={() => navigate(`/patient-phi/${params.data._id}`)}
                color="primary"
                size="small"
                aria-label="view"
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                onClick={() => navigate(`/patient-phi/${params.data._id}/edit`)}
                color="primary"
                size="small"
                aria-label="edit"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                onClick={() => {
                  setDeleteRow(params.data);
                  setDeleteOpen(true);
                }}
                color="error"
                size="small"
                aria-label="delete"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [navigate],
  );

  return (
    <div className={PatientPHIStyle.page}>
      <DeleteDialog
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setDeleteRow(null);
        }}
        onConfirm={handleConfirmDelete}
        compact
      />
      <Box className={PatientPHIStyle.pageHead}>
        <Typography className={PatientPHIStyle.pageTitle}>Patient PHI</Typography>
      </Box>
      <Card className={PatientPHIStyle.tableCard}>
        <div className={PatientPHIStyle.tableHeader}>
          <h2 className={PatientPHIStyle.tableTitle}>Patient PHI List</h2>
          <div className={PatientPHIStyle.actions}>
            <TextField
              size="small"
              className={PatientPHIStyle.searchField}
              placeholder="Search name / mobile / ID"
              value={localSearch}
              onChange={(e) => {
                setPage(0);
                setLocalSearch(e.target.value);
              }}
            />
            <Button
              className={PatientPHIStyle.addBtn}
              variant="contained"
              size="small"
              startIcon={<HealthAndSafetyIcon />}
              onClick={() => navigate('/patient-phi/new')}
            >
              Add New
            </Button>
          </div>
        </div>
        <CommonAgGrid
          rowData={records || []}
          columnDefs={columns}
          getRowId={(p) => String(p.data._id)}
          loading={loading}
          height={320}
          pagination
          paginationMode="server"
          rowCount={Number(totalCount) || 0}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[5, 10, 25, 50, 100]}
        />
      </Card>
    </div>
  );
}
